'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { TRPC_SETTINGS } from '~/constants/trpc';
import { api } from '~/libs/api';
export const ACCOMMODATION_DRAWERS_STALE_TIME_MS = 5 * 60 * 1000;
const PREFETCH_DEBOUNCE_MS = 500;
const SCROLL_IDLE_MS = 150;
let isWindowScrolling = false;
let scrollEndTimer: ReturnType<typeof setTimeout> | null = null;
let scrollSubscriberCount = 0;
const scrollIdleListeners = new Set<() => void>();
const notifyScrollIdle = () => {
  for (const listener of scrollIdleListeners) {
    listener();
  }
};
const onWindowScroll = () => {
  isWindowScrolling = true;
  if (scrollEndTimer) {
    clearTimeout(scrollEndTimer);
  }
  scrollEndTimer = setTimeout(() => {
    isWindowScrolling = false;
    notifyScrollIdle();
  }, SCROLL_IDLE_MS);
};
const subscribeWindowScrollIdle = () => {
  scrollSubscriberCount += 1;
  if (scrollSubscriberCount === 1) {
    globalThis.window.addEventListener('scroll', onWindowScroll, {
      passive: true,
      capture: true
    });
  }
  return () => {
    scrollSubscriberCount -= 1;
    if (scrollSubscriberCount === 0) {
      globalThis.window.removeEventListener('scroll', onWindowScroll, {
        capture: true
      });
      if (scrollEndTimer) {
        clearTimeout(scrollEndTimer);
        scrollEndTimer = null;
      }
      isWindowScrolling = false;
    }
  };
};
type UsePrefetchAccommodationDrawersOnInViewOptions = {
  tripId: string;
  itemId: number;
  enabled?: boolean;
};
export const usePrefetchAccommodationDrawersOnInView = t0 => {
  const $ = _c(16);
  const {
    tripId,
    itemId,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const utils = api.useUtils();
  const prefetchTimeoutRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      threshold: 0.1,
      rootMargin: "200px",
      triggerOnce: false
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    ref,
    inView
  } = useInView(t2);
  let t3;
  if ($[1] !== itemId || $[2] !== tripId || $[3] !== utils) {
    t3 = () => {
      if (!tripId || !itemId) {
        return;
      }
      utils.trip.getAlternativeRoomsForStayItem.prefetch({
        journeyId: tripId,
        itemId,
        deduplicated: true
      }, {
        ...TRPC_SETTINGS.QUERY_NO_REFETCH,
        staleTime: ACCOMMODATION_DRAWERS_STALE_TIME_MS
      });
    };
    $[1] = itemId;
    $[2] = tripId;
    $[3] = utils;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const prefetchRooms = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(_temp, t4);
  let t5;
  let t6;
  if ($[6] !== enabled || $[7] !== inView || $[8] !== itemId || $[9] !== tripId || $[10] !== utils) {
    t5 = () => {
      const schedulePrefetch = () => {
        if (!enabled || !inView || !tripId || !itemId) {
          return;
        }
        if (prefetchTimeoutRef.current) {
          clearTimeout(prefetchTimeoutRef.current);
        }
        prefetchTimeoutRef.current = setTimeout(() => {
          if (isWindowScrolling) {
            return;
          }
          utils.trip.getAlternativeAccommodation.prefetch({
            journeyId: tripId,
            itemId
          }, {
            ...TRPC_SETTINGS.QUERY_NO_REFETCH,
            staleTime: ACCOMMODATION_DRAWERS_STALE_TIME_MS
          });
        }, PREFETCH_DEBOUNCE_MS);
      };
      const clearScheduledPrefetch = () => {
        if (prefetchTimeoutRef.current) {
          clearTimeout(prefetchTimeoutRef.current);
          prefetchTimeoutRef.current = null;
        }
      };
      if (!enabled || !inView || !tripId || !itemId) {
        clearScheduledPrefetch();
        return clearScheduledPrefetch;
      }
      schedulePrefetch();
      scrollIdleListeners.add(schedulePrefetch);
      return () => {
        scrollIdleListeners.delete(schedulePrefetch);
        clearScheduledPrefetch();
      };
    };
    t6 = [enabled, inView, tripId, itemId, utils];
    $[6] = enabled;
    $[7] = inView;
    $[8] = itemId;
    $[9] = tripId;
    $[10] = utils;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== prefetchRooms || $[14] !== ref) {
    t7 = {
      ref,
      prefetchRooms
    };
    $[13] = prefetchRooms;
    $[14] = ref;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
function _temp() {
  return subscribeWindowScrollIdle();
}